<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1">{{ $t('post') }}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <!-- Image -->
                <div role="group" class="form-group">
                <label class=""> {{ $t('imageMandatory') }}</label>
                    <GalleryPicker class="gallery-picker" v-model="post.image" :ratioWidth="16" :ratioHeight="9" v-bind:class="{ 'is-invalid': genericValidator(post.image, attemptedSubmit) === false || errorValidator('Image') === false }" :max="1"></GalleryPicker>
                </div>
                <!-- Categories -->
                <div role="group" class="form-group">
                <label class=""> {{ $t('categoriesMandatory') }}</label>
                    <OptionsDropdown v-model="post.categories" :options="categoriesOptions" :title="$t('categories')" :index="0" :is-valid="genericValidator(post.categories, attemptedSubmit) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></OptionsDropdown>
                </div>
                <!-- Titles -->
                <div role="group" class="form-group">
                <label class=""> {{ $t('titleMandatory') }}</label>
                    <CInput type="text" v-model="post.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(post.titles, attemptedSubmit) && errorValidator('Titles')"></CInput>
                </div>
                <!-- Bodies -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('bodyMandatory') }}</label>
                    <vue-editor v-model="post.bodies[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(post.bodies, attemptedSubmit) == false || errorValidator('Bodies') }"></vue-editor>
                    <div v-if="errorOutput['Bodies']" class="invalid-feedback">{{errorOutput['Bodies']}}</div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapActions, mapState } from 'vuex'
import router from '@/router/index';

import { API_HELPER } from "@/helpers/api.js";
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";

import Post from "@/classes/Post";
import InputGetList from "@/classes/InputGetList";

import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { VueEditor } from "vue2-editor";

  export default {
    components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, OptionsDropdown, FormLoading, VueEditor },
    props:{    
        basePost:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            post: new Post({}, {}, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
        }
    },
    mounted: function()
    {
        this.resetPost();
        this.getAllCategories();
    },
    computed:
    {
        ...mapState('posts', ['putPostOutput']),
        ...mapState('posts', ['postPostOutput']),
        ...mapState('categories', ['getCategoriesOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.post.titles)) { if(!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.post.bodies)) { if(!value) { result['Bodies'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        name: function () {
            return this.basePost || this.baseName ? (this.basePost ? this.basePost.name : this.baseName) : null;
        },
        categoriesInfo: function() {
            return this.getCategoriesOutput && this.getCategoriesOutput.data ? this.getCategoriesOutput.data : null;
        },
        categoriesOptions: function()
        {
            var result = [];

            if(this.categoriesInfo)
            {
                this.categoriesInfo.forEach(category => 
                {
                    result.push({key: category.id, label: category.name });
                });
            }

            return result;
        }, 
    },
    methods:{
        ...mapActions('categories', ['getCategories']),
        genericValidator,
        errorValidator,
        resetPost(){
            if(!this.basePost)
            {
                var titles = {}; var bodies = {}; 
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; bodies[language.key] = "";  });
                this.post = new Post(titles, bodies, []);
            }
            else
            {
                this.post = deepClone(new Post, this.basePost); 
                this.post.categories = this.post.categories.map(x=> x.id);   
                this.post.image = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.post.image])};
            }
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.post.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.post);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "PostsList"});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        },
        getAllCategories(){
            const itemsPerPage = 999;
            const currentPage = 1;
            const searchText = null;
            const sorter = 2; // DESC
            const propertyName = "Name";

            var input = new InputGetList(itemsPerPage, currentPage, searchText, sorter, propertyName);
            input.type = 1; // type -> post
            input.level = 1; // Parent

            this.getCategories(input);         
        },
    },
    watch:
    {
        putPostOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postPostOutput: function(val)
        {
            this.onProcessTask(val);
        },
        basePost: function()
        {
            this.resetPost();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 